import { useState, useCallback, KeyboardEvent } from 'react';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLogin: (password: string) => Promise<boolean>;
  title?: string;
  description?: string;
}

export function LoginModal({
  isOpen,
  onClose,
  onLogin,
  title = 'Enter Password to Edit',
  description = 'You need to log in to make changes.',
}: LoginModalProps) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    setError(false);
    const success = await onLogin(password);
    setLoading(false);
    if (success) {
      setPassword('');
      onClose();
    } else {
      setError(true);
    }
  }, [password, onLogin, onClose]);

  const handleKeyPress = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  const handleClose = useCallback(() => {
    setPassword('');
    setError(false);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div className={`modal-overlay ${isOpen ? 'modal-overlay--show' : ''}`}>
      <div className="modal">
        <h2 className="modal__title">{title}</h2>
        <p className="modal__description">{description}</p>
        <input
          type="password"
          className="modal__input"
          placeholder="Enter password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyPress={handleKeyPress}
          autoFocus
        />
        <div className="modal__buttons">
          <button className="btn-secondary" onClick={handleClose} disabled={loading}>
            Cancel
          </button>
          <button className="btn-primary" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </div>
        {error && <p className="modal__error">Incorrect password</p>}
      </div>
    </div>
  );
}
